import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { Handle, Position } from 'reactflow'
import { cn } from '@/lib/utils'
import { Image, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from "@/components/ui/badge"
import { API_URL } from '@/config'
import { withClerkAuthHeader } from '@/lib/clerk-transport'

interface PhotoNodeData {
  id: string
  imageB64?: string
  input?: string
  onImageGenerated?: (imageB64: string) => void
  onCreateDataNode?: (nodeId: string, imageB64: string) => void
  hasDataNode?: boolean
}

export function PhotoNode({ id, data }: { id: string; data: PhotoNodeData }) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [input, setInput] = useState<string>(data.input || '')
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [hasDataNode, setHasDataNode] = useState(data.hasDataNode || false)
  const { getToken } = useAuth()

  useEffect(() => {
    console.log('PhotoNode data changed:', { dataInput: data.input, currentInput: input, isLoading });
    // Only update the input if the user hasn't started editing
    if (data.input && !hasUserEdited && data.input !== input && !isLoading) {
      console.log('Initializing input from data.input:', data.input);
      setInput(data.input);
    }
  }, [data.input, input, isLoading, hasUserEdited]);

  // Check for session ID on mount and when data changes
  useEffect(() => {
    const sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      console.error('No session ID found on mount');
      setError('Session not initialized. Please refresh the page.');
    }
  }, []);

  const isActionPhase = !data.imageB64 || isLoading
  const hasError = !!error

  const isValidUrl = (str: string) => {
    try {
      new URL(str)
      return true
    } catch {
      return false
    }
  }

  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    const response = await fetch(url)
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64 = reader.result as string
        // Remove the data:image/*;base64, prefix
        resolve(base64.split(',')[1])
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  const processInput = async () => {
    if (!input.trim()) return
    
    console.log('Starting to process input:', input);
    setIsLoading(true);
    setError(null);

    try {
      if (isValidUrl(input)) {
        // Fetch and convert image to base64 directly
        const imageB64 = await fetchImageAsBase64(input.trim());
        console.log('Generated image from URL, triggering callback');
        if (data.onImageGenerated) {
          data.onImageGenerated(imageB64);
        }
        // Update the node data
        data.imageB64 = imageB64;
      } else {
        // Check for session ID
        const sessionId = localStorage.getItem('session_id');
        if (!sessionId) {
          throw new Error('No session ID found. Please refresh the page.');
        }
        
        console.log('Sending image request with session ID:', sessionId);
        const token = await getToken()
        const headers = withClerkAuthHeader(
          {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          token
        )
        
        const response = await fetch(`${API_URL}/ws/${sessionId}/image`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            prompt: input.trim(),
            size: '512x512',
            n: 1,
          }),
        });

        if (!response.ok) {
          const error = await response.text();
          console.error('Image generation failed:', {
            status: response.status,
            error,
            sessionId,
            url: `${API_URL}/ws/${sessionId}/image`,
          });
          throw new Error(`Failed to generate image: ${error}`);
        }

        const responseData = await response.json();
        console.log('Generated image from API, triggering callback');
        if (data.onImageGenerated) {
          data.onImageGenerated(responseData.image_b64);
        }
        // Update the node data
        data.imageB64 = responseData.image_b64;
      }
    } catch (err) {
      console.error('Error processing image:', err);
      setError(err instanceof Error ? err.message : 'Failed to process image');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="relative">
      <div
        className={cn(
          "group relative w-[300px]",
          "bg-card border rounded-lg shadow-lg transition-all",
          isActionPhase && 'opacity-50',
          hasError && 'border-red-500'
        )}
        draggable={true}
      >
        <Handle
          type="target"
          position={Position.Top}
          style={{ width: '10px', height: '10px', background: '#555' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ width: '10px', height: '10px', background: '#555' }}
        />

        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Photo Generator</span>
              <Badge variant={isActionPhase ? "outline" : "secondary"} className="h-5">
                {isActionPhase ? "Waiting" : "Ready"}
              </Badge>
            </div>
            {data.imageB64 && (
              <Button
                variant={hasDataNode ? "secondary" : "ghost"}
                size="sm"
                className="h-8 px-2"
                onClick={() => {
                  const newState = !hasDataNode;
                  setHasDataNode(newState);
                  data.hasDataNode = newState;
                  if (newState && data.onCreateDataNode) {
                    data.onCreateDataNode(id, data.imageB64);
                  }
                }}
              >
                {hasDataNode ? "Data Node Created" : "Create Data Node"}
              </Button>
            )}
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : data.imageB64 ? (
            <div className="space-y-4">
              <div className="relative aspect-square rounded-lg overflow-hidden bg-secondary/10">
                <img 
                  src={`data:image/jpeg;base64,${data.imageB64}`}
                  alt="Generated or fetched"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => { setInput(e.target.value); setHasUserEdited(true); }}
                  placeholder="Enter URL or prompt..."
                  className="text-sm flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      processInput()
                    }
                  }}
                />
                <Button 
                  size="default"
                  onClick={processInput}
                  disabled={isLoading}
                >
                  Go
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => { setInput(e.target.value); setHasUserEdited(true); }}
                  placeholder="Enter URL or prompt..."
                  className="text-sm flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      processInput()
                    }
                  }}
                />
                <Button 
                  size="default"
                  onClick={processInput}
                  disabled={isLoading}
                >
                  Go
                </Button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <p className="text-xs text-red-500">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
